(() => {
  'use strict';

  const DESKTOP_QUERY = '(min-width: 1024px)';
  const DEFAULT_COMPRESSION_DISTANCE = 280;
  const SCROLL_SMOOTHING_MS = 72;
  const SCROLL_EPSILON = 0.00015;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (start, end, progress) => start + (end - start) * progress;
  const smoothstep = (progress) => {
    const value = clamp(progress);
    return value * value * (3 - 2 * value);
  };
  const px = (value) => `${Math.round(value * 1000) / 1000}px`;

  const parseNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseColor = (value, fallback = [255, 255, 255, 1]) => {
    if (!value || value === 'transparent') return [0, 0, 0, 0];
    const match = String(value).match(
      /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+))?\s*\)/i
    );
    if (!match) return fallback;
    return [
      Number.parseFloat(match[1]),
      Number.parseFloat(match[2]),
      Number.parseFloat(match[3]),
      match[4] === undefined ? 1 : Number.parseFloat(match[4])
    ];
  };

  const mixColor = (start, end, progress) => {
    const values = start.map((value, index) => lerp(value, end[index], progress));
    return `rgba(${Math.round(values[0])}, ${Math.round(values[1])}, ${Math.round(
      values[2]
    )}, ${values[3].toFixed(3)})`;
  };

  const closeDisclosureSiblings = (current, header) => {
    header.querySelectorAll('[data-pws-disclosure][open]').forEach((details) => {
      if (details !== current) details.removeAttribute('open');
    });
  };

  const initHeader = (header) => {
    if (!header || header.dataset.pwsInitialized === 'true') return;
    header.dataset.pwsInitialized = 'true';

    const body = document.body;
    const root = document.documentElement;
    const sectionWrapper = header.closest('.shopify-section') || header.parentElement;
    const desktopMedia = window.matchMedia(DESKTOP_QUERY);
    const stickyEnabled = header.dataset.pwsSticky === 'true';
    const compactEnabled = header.dataset.pwsCompactEnabled === 'true';
    const compressionStart = Math.max(
      0,
      Number.parseInt(header.dataset.pwsScrollThreshold || '24', 10) || 0
    );
    const compressionDistance = Math.max(
      80,
      Number.parseInt(
        header.dataset.pwsCompressionDistance || String(DEFAULT_COMPRESSION_DISTANCE),
        10
      ) || DEFAULT_COMPRESSION_DISTANCE
    );

    const headerStyle = window.getComputedStyle(header);
    const compactMenuOffsetX = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-menu-offset-x'),
      0
    );
    const compactMenuOffsetY = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-menu-offset-y'),
      0
    );
    const compactLogoOffsetX = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-logo-offset-x'),
      0
    );
    const compactLogoOffsetY = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-logo-offset-y'),
      0
    );
    const compactActionsOffsetX = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-actions-offset-x'),
      0
    );
    const compactActionsOffsetY = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-actions-offset-y'),
      0
    );
    const compactSearchIconSize = Math.max(
      28,
      parseNumber(headerStyle.getPropertyValue('--pws-compact-search-icon-size'), 42)
    );
    const compactSearchIconGap = Math.max(
      0,
      parseNumber(headerStyle.getPropertyValue('--pws-compact-search-icon-gap'), 14)
    );
    const compactSearchIconOffsetX = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-search-icon-offset-x'),
      0
    );
    const compactSearchIconOffsetY = parseNumber(
      headerStyle.getPropertyValue('--pws-compact-search-icon-offset-y'),
      0
    );

    const layout = header.querySelector('.pws-header__layout');
    const logo = header.querySelector('.pws-header__logo');
    const logoLink = logo ? logo.querySelector('.site-logo') : null;
    const navigationRow = header.querySelector('.pws-header__nav-row');
    const navigation = header.querySelector('.pws-header__navigation');
    const navList = navigation ? navigation.querySelector('.navmenu-depth-1') : null;
    const navLinks = Array.from(
      navigation ? navigation.querySelectorAll('.navmenu-link-depth-1') : []
    );
    const actions = header.querySelector('.pws-header__actions');
    const accountText = Array.from(
      header.querySelectorAll('.site-header_account-link-text, .site-header__account-links')
    );
    const socials = header.querySelector('.pws-header__socials');
    const localization = header.querySelector('.pws-header__localization');
    const compactSearchToggle = header.querySelector('[data-pws-search-toggle]');
    const searchClose = header.querySelector('[data-pws-search-close]');
    const searchShell = header.querySelector('[data-pws-search-shell]');
    const searchInput = searchShell ? searchShell.querySelector('[data-live-search-input]') : null;
    const searchSubmit = searchShell ? searchShell.querySelector('[data-live-search-submit]') : null;
    const searchSubmitIcon = searchSubmit ? searchSubmit.querySelector('.search-icon--inactive svg') : null;
    const searchForm = searchShell ? searchShell.querySelector('.live-search-form') : null;
    const searchField = searchShell ? searchShell.querySelector('.live-search-form-field') : null;
    const searchFilter = searchShell ? searchShell.querySelector('.live-search-filter-wrapper') : null;
    const searchCancel = searchShell ? searchShell.querySelector('.live-search-takeover-cancel') : null;
    const searchFlydown = searchShell ? searchShell.querySelector('.search-flydown') : null;
    const megaItems = Array.from(header.querySelectorAll('[data-navmenu-meganav-trigger]'));
    const megaDetails = megaItems
      .map((item) => item.querySelector(':scope > [data-navmenu-details]'))
      .filter(Boolean);
    const topLevelMenuDetails = Array.from(
      navigation
        ? navigation.querySelectorAll('.navmenu-depth-1 > .navmenu-item > [data-navmenu-details]')
        : []
    );
    const basicDropdownDetails = topLevelMenuDetails.filter(
      (details) => !details.closest('[data-navmenu-meganav-trigger]')
    );
    const megaSubmenus = megaItems
      .map((item) => item.querySelector('[data-meganav-menu]'))
      .filter(Boolean);
    const megaOverlay = header.querySelector('[data-pws-mega-overlay]');
    const disclosures = Array.from(header.querySelectorAll('[data-pws-disclosure]'));

    /* Mantiene IDENTICA la gestione dell'overlay del tema sincronizzato. */
    if (megaOverlay) {
      document.querySelectorAll('body > [data-pws-mega-overlay]').forEach((existingOverlay) => {
        if (existingOverlay !== megaOverlay) existingOverlay.remove();
      });
      body.appendChild(megaOverlay);
    }

    if (sectionWrapper) sectionWrapper.classList.add('pws-header-section');

    root.style.setProperty('--pws-mega-blur', `${Number(header.dataset.pwsMegaBlur || 4)}px`);
    root.style.setProperty('--pws-mega-dim', String(header.dataset.pwsMegaDim || 0.24));

    body.classList.toggle('site-header-sticky', stickyEnabled);
    body.classList.toggle('pws-header-sticky-enabled', stickyEnabled);

    let geometry = null;
    let scrollProgress = 0;
    let targetScrollProgress = 0;
    let renderedScrollProgress = 0;
    let scrollAnimationFrame = null;
    let lastScrollFrameTime = 0;
    let scrollModeActivated = false;
    let resizeTicking = false;
    let isMeasuring = false;

    const spacer = document.createElement('div');
    spacer.className = 'pws-header__scroll-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    header.insertAdjacentElement('afterend', spacer);

    const getRect = (element) => {
      if (!element) return null;
      const rect = element.getBoundingClientRect();
      return {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height
      };
    };

    const relativeRect = (rect, parentRect) => {
      if (!rect || !parentRect) return null;
      return {
        top: rect.top - parentRect.top,
        left: rect.left - parentRect.left,
        width: rect.width,
        height: rect.height
      };
    };

    const readBox = (element) => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      return {
        paddingTop: parseNumber(style.paddingTop),
        paddingRight: parseNumber(style.paddingRight),
        paddingBottom: parseNumber(style.paddingBottom),
        paddingLeft: parseNumber(style.paddingLeft),
        gap: parseNumber(style.columnGap || style.gap),
        fontSize: parseNumber(style.fontSize)
      };
    };

    const readSearchButton = () => {
      if (!searchSubmit) return null;
      const style = window.getComputedStyle(searchSubmit);
      return {
        background: parseColor(style.backgroundColor, [233, 173, 20, 1]),
        border: parseColor(style.borderTopColor, [233, 173, 20, 1]),
        color: parseColor(style.color, [18, 18, 18, 1]),
        width: parseNumber(style.width, 54),
        radius: parseNumber(style.borderTopRightRadius, 4)
      };
    };

    const captureGeometryState = () => {
      const headerRect = getRect(header);
      const layoutRect = getRect(layout);
      const navRowRect = getRect(navigationRow);
      return {
        header: headerRect,
        layout: layoutRect,
        logo: relativeRect(getRect(logo), layoutRect),
        logoLink: relativeRect(getRect(logoLink), layoutRect),
        searchShell: relativeRect(getRect(searchShell), layoutRect),
        searchSubmit: relativeRect(getRect(searchSubmit), layoutRect),
        compactSearchToggle: relativeRect(getRect(compactSearchToggle), layoutRect),
        searchSubmitViewport: getRect(searchSubmit),
        compactSearchToggleViewport: getRect(compactSearchToggle),
        searchSubmitIconViewport: getRect(searchSubmitIcon),
        actions: relativeRect(getRect(actions), layoutRect),
        actionsViewport: getRect(actions),
        navRow: relativeRect(navRowRect, layoutRect),
        navigation: relativeRect(getRect(navigation), navRowRect),
        navigationLayout: relativeRect(getRect(navigation), layoutRect),
        socials: relativeRect(getRect(socials), navRowRect),
        localization: relativeRect(getRect(localization), navRowRect),
        socialsLayout: relativeRect(getRect(socials), layoutRect),
        localizationLayout: relativeRect(getRect(localization), layoutRect),
        navRowBox: readBox(navigationRow),
        navListBox: readBox(navList),
        navLinkBox: navLinks.length ? readBox(navLinks[0]) : null,
        searchButton: readSearchButton()
      };
    };

    const forceLayout = () => header.getBoundingClientRect();

    const measureGeometry = () => {
      if (!desktopMedia.matches || !layout || !navigationRow || !navigation) return;
      isMeasuring = true;

      const savedScrollProgress = scrollProgress;
      clearScrollPresentation(false);
      header.classList.add('pws-header--scroll-measuring', 'pws-header--flip-prep');
      header.classList.remove('pws-header--compact', 'pws-header--search-open');
      body.classList.remove('pws-header-is-compact');
      forceLayout();
      const expanded = captureGeometryState();

      header.classList.add('pws-header--compact');
      body.classList.add('pws-header-is-compact');
      forceLayout();
      const compact = captureGeometryState();

      header.classList.remove('pws-header--compact', 'pws-header--scroll-measuring', 'pws-header--flip-prep');
      body.classList.remove('pws-header-is-compact');
      forceLayout();

      geometry = { expanded, compact };
      scrollProgress = savedScrollProgress;
      isMeasuring = false;
      applyScrollProgress(scrollProgress);
    };

    const setBox = (element, rect) => {
      if (!element || !rect) return;
      element.style.position = 'absolute';
      element.style.top = px(rect.top);
      element.style.left = px(rect.left);
      element.style.width = px(Math.max(0, rect.width));
      element.style.height = px(Math.max(0, rect.height));
      element.style.margin = '0';
    };

    const interpolateRect = (start, end, progress) => {
      if (!start || !end) return start || end;
      return {
        top: lerp(start.top, end.top, progress),
        left: lerp(start.left, end.left, progress),
        width: lerp(start.width, end.width, progress),
        height: lerp(start.height, end.height, progress)
      };
    };

    const clearStyles = (element, properties) => {
      if (!element) return;
      properties.forEach((property) => element.style.removeProperty(property));
    };

    function clearScrollPresentation(removeSpacer = true) {
      header.classList.remove(
        'pws-header--scroll-linked',
        'pws-header--scroll-active',
        'pws-header--scroll-complete',
        'pws-header--search-open'
      );
      body.classList.remove('pws-header-scroll-linked', 'pws-header-is-compact');

      clearStyles(header, ['height', 'box-shadow', '--pws-search-field-opacity']);
      clearStyles(layout, ['position', 'display', 'height', 'min-height', 'padding']);

      [logo, logoLink, searchShell, actions, navigationRow, navigation, socials, localization].forEach(
        (element) => {
          clearStyles(element, [
            'position',
            'top',
            'left',
            'right',
            'width',
            'height',
            'margin',
            'padding-top',
            'padding-right',
            'padding-bottom',
            'padding-left',
            'opacity',
            'visibility',
            'pointer-events',
            'display',
            'z-index',
            'overflow',
            'background-color'
          ]);
        }
      );

      clearStyles(navList, ['gap']);
      navLinks.forEach((link) =>
        clearStyles(link, ['padding-top', 'padding-bottom', 'font-size'])
      );
      accountText.forEach((element) =>
        clearStyles(element, ['opacity', 'visibility', 'pointer-events'])
      );
      [searchField, searchFilter, searchCancel, searchFlydown].forEach((element) =>
        clearStyles(element, ['opacity', 'visibility', 'pointer-events'])
      );
      clearStyles(searchForm, ['border-color', 'box-shadow']);
      clearStyles(searchSubmit, [
        'position',
        'top',
        'right',
        'bottom',
        'left',
        'width',
        'height',
        'margin',
        'color',
        'background-color',
        'border-color',
        'border-radius',
        'padding',
        'opacity',
        'visibility',
        'pointer-events',
        'transform',
        'z-index',
        '--pws-search-icon-current-size'
      ]);

      if (compactSearchToggle) {
        compactSearchToggle.setAttribute('aria-expanded', 'false');
      }
      if (searchSubmit) searchSubmit.setAttribute('aria-expanded', 'false');
      if (removeSpacer) spacer.style.height = '0px';
    }

    const updateMetrics = () => {
      const rect = header.getBoundingClientRect();
      const height = Math.max(0, rect.height);
      const bottom = Math.max(0, Math.min(window.innerHeight, rect.bottom));
      root.style.setProperty('--pws-header-height', `${height}px`);
      root.style.setProperty('--pws-header-bottom', `${bottom}px`);
      if (megaOverlay) megaOverlay.style.top = `${bottom}px`;
    };

    const setSearchOpen = (open) => {
      const shouldOpen = Boolean(open && desktopMedia.matches && scrollProgress >= 0.995);
      header.classList.toggle('pws-header--search-open', shouldOpen);
      if (compactSearchToggle) compactSearchToggle.setAttribute('aria-expanded', String(shouldOpen));
      if (searchSubmit) searchSubmit.setAttribute('aria-expanded', String(shouldOpen));

      if (shouldOpen) {
        clearStyles(searchSubmit, [
          'position',
          'top',
          'right',
          'bottom',
          'left',
          'margin',
          'opacity',
          'visibility',
          'pointer-events',
          'transform',
          'z-index'
        ]);

        if (searchInput) {
          window.requestAnimationFrame(() => searchInput.focus());
        }
      } else {
        if (searchSubmit && typeof searchSubmit.blur === 'function') searchSubmit.blur();
        if (!isMeasuring) applyScrollProgress(scrollProgress);
      }
    };

    function applyScrollProgress(progress) {
      if (isMeasuring || !geometry || !desktopMedia.matches || !stickyEnabled || !compactEnabled) {
        return;
      }

      const normalized = clamp(progress);
      scrollProgress = normalized;

      if (normalized <= 0.0001 && !scrollModeActivated) {
        clearScrollPresentation(true);
        updateMetrics();
        return;
      }

      const { expanded, compact } = geometry;
      const currentHeaderHeight = lerp(expanded.header.height, compact.header.height, normalized);
      const hiddenProgress = smoothstep(clamp((normalized - 0.02) / 0.78));
      const searchFieldOpacity = 1 - smoothstep(clamp(normalized / 0.78));
      const shadowOpacity = 0.18 * normalized;

      header.classList.add('pws-header--scroll-linked');
      header.classList.toggle('pws-header--scroll-active', normalized > 0.0001);
      header.classList.toggle('pws-header--scroll-complete', normalized >= 0.995);
      body.classList.add('pws-header-scroll-linked');
      body.classList.toggle('pws-header-is-compact', normalized >= 0.995);

      header.style.height = px(currentHeaderHeight);
      header.style.boxShadow = `0 ${px(8 * normalized)} ${px(24 * normalized)} rgba(0, 0, 0, ${shadowOpacity.toFixed(3)})`;
      header.style.setProperty('--pws-search-field-opacity', String(searchFieldOpacity));
      spacer.style.height = px(expanded.header.height);

      layout.style.position = 'relative';
      layout.style.display = 'block';
      layout.style.height = '100%';
      layout.style.minHeight = '0';
      layout.style.padding = '0';

      const currentLogoRect = interpolateRect(expanded.logo, compact.logo, normalized);
      currentLogoRect.left += compactLogoOffsetX * normalized;
      currentLogoRect.top += compactLogoOffsetY * normalized;
      setBox(logo, currentLogoRect);
      if (logoLink && expanded.logoLink && compact.logoLink) {
        logoLink.style.width = px(lerp(expanded.logoLink.width, compact.logoLink.width, normalized));
        logoLink.style.maxWidth = logoLink.style.width;
        logoLink.style.height = '100%';
      }

      const currentActionsRect = interpolateRect(expanded.actions, compact.actions, normalized);
      currentActionsRect.left += compactActionsOffsetX * normalized;
      currentActionsRect.top += compactActionsOffsetY * normalized;
      setBox(actions, currentActionsRect);

      /*
       * The navigation carrier always covers the whole header and stays transparent.
       * Only the menu itself moves vertically. This removes the black rectangle that
       * previously shrank toward the centre and prevents any sideways drift.
       */
      const currentNavTop = lerp(
        expanded.navigationLayout.top,
        compact.navigationLayout.top + compactMenuOffsetY,
        normalized
      );
      const currentNavWidth = lerp(
        expanded.navigationLayout.width,
        compact.navigationLayout.width,
        normalized
      );
      const fixedNavCenter =
        expanded.navigationLayout.left + expanded.navigationLayout.width / 2;
      const currentNavLeft =
        fixedNavCenter - currentNavWidth / 2 + compactMenuOffsetX * normalized;
      const currentNavHeight = lerp(
        expanded.navigationLayout.height,
        compact.navigationLayout.height,
        normalized
      );

      setBox(navigationRow, {
        top: 0,
        left: 0,
        width: expanded.layout.width,
        height: currentHeaderHeight
      });
      navigationRow.style.padding = '0';
      navigationRow.style.backgroundColor = 'transparent';
      navigationRow.style.pointerEvents = 'none';
      navigationRow.style.overflow = 'visible';

      setBox(navigation, {
        top: currentNavTop,
        left: currentNavLeft,
        width: currentNavWidth,
        height: currentNavHeight
      });
      navigation.style.pointerEvents = 'auto';

      if (navList && expanded.navListBox && compact.navListBox) {
        navList.style.gap = px(
          lerp(expanded.navListBox.gap, compact.navListBox.gap, normalized)
        );
      }

      if (expanded.navLinkBox && compact.navLinkBox) {
        navLinks.forEach((link) => {
          link.style.paddingTop = px(
            lerp(expanded.navLinkBox.paddingTop, compact.navLinkBox.paddingTop, normalized)
          );
          link.style.paddingBottom = px(
            lerp(expanded.navLinkBox.paddingBottom, compact.navLinkBox.paddingBottom, normalized)
          );
          link.style.fontSize = px(
            lerp(expanded.navLinkBox.fontSize, compact.navLinkBox.fontSize, normalized)
          );
        });
      }

      if (socials && expanded.socialsLayout) {
        setBox(socials, expanded.socialsLayout);
        socials.style.opacity = String(1 - hiddenProgress);
        socials.style.visibility = hiddenProgress >= 0.995 ? 'hidden' : 'visible';
        socials.style.pointerEvents = hiddenProgress >= 0.8 ? 'none' : 'auto';
      }

      if (localization && expanded.localizationLayout) {
        setBox(localization, expanded.localizationLayout);
        localization.style.opacity = String(1 - hiddenProgress);
        localization.style.visibility = hiddenProgress >= 0.995 ? 'hidden' : 'visible';
        localization.style.pointerEvents = hiddenProgress >= 0.8 ? 'none' : 'auto';
      }

      const compactActionsViewport = compact.actionsViewport;
      const searchTargetViewport = compactActionsViewport
        ? {
            top:
              compactActionsViewport.top +
              (compactActionsViewport.height - compactSearchIconSize) / 2 +
              compactSearchIconOffsetY +
              compactActionsOffsetY,
            left:
              compactActionsViewport.left -
              compactSearchIconGap -
              compactSearchIconSize +
              compactSearchIconOffsetX +
              compactActionsOffsetX,
            width: compactSearchIconSize,
            height: compactSearchIconSize
          }
        : compact.compactSearchToggleViewport || compact.searchSubmitViewport;
      if (!header.classList.contains('pws-header--search-open')) {
        /* The white search field dissolves in its original position. Only the original
         * yellow search button travels to its compact white-icon destination. */
        setBox(searchShell, expanded.searchShell);
        searchShell.style.overflow = 'visible';

        if (searchFieldOpacity <= 0.015) {
          searchShell.style.width = '0px';
          searchShell.style.height = '0px';
        }
      }

      if (compactSearchToggle) {
        compactSearchToggle.style.display = 'none';
        compactSearchToggle.style.opacity = '0';
        compactSearchToggle.style.visibility = 'hidden';
        compactSearchToggle.style.pointerEvents = 'none';
      }

      [searchField, searchFilter, searchCancel].forEach((element) => {
        if (!element) return;
        element.style.opacity = String(searchFieldOpacity);
        element.style.visibility = searchFieldOpacity <= 0.015 ? 'hidden' : 'visible';
        element.style.pointerEvents = searchFieldOpacity <= 0.15 ? 'none' : 'auto';
      });
      if (searchFlydown) {
        searchFlydown.style.opacity = '0';
        searchFlydown.style.visibility = 'hidden';
        searchFlydown.style.pointerEvents = 'none';
      }
      if (searchForm) {
        searchForm.style.borderColor = `rgba(128, 128, 128, ${(0.2 * searchFieldOpacity).toFixed(3)})`;
        searchForm.style.boxShadow = `0 2px 3px rgba(128, 128, 128, ${(0.11 * searchFieldOpacity).toFixed(3)})`;
      }

      if (
        searchSubmit &&
        expanded.searchButton &&
        expanded.searchSubmitViewport &&
        searchTargetViewport &&
        !header.classList.contains('pws-header--search-open')
      ) {
        const compactColor = [255, 255, 255, 1];
        const transparent = [18, 18, 18, 0];
        const currentSearchButton = interpolateRect(
          expanded.searchSubmitViewport,
          searchTargetViewport,
          normalized
        );

        searchSubmit.style.position = 'fixed';
        searchSubmit.style.top = px(currentSearchButton.top);
        searchSubmit.style.right = 'auto';
        searchSubmit.style.bottom = 'auto';
        searchSubmit.style.left = px(currentSearchButton.left);
        searchSubmit.style.width = px(currentSearchButton.width);
        searchSubmit.style.height = px(currentSearchButton.height);
        searchSubmit.style.margin = '0';
        searchSubmit.style.backgroundColor = mixColor(
          expanded.searchButton.background,
          transparent,
          normalized
        );
        searchSubmit.style.borderColor = mixColor(
          expanded.searchButton.border,
          transparent,
          normalized
        );
        searchSubmit.style.color = mixColor(
          expanded.searchButton.color,
          compactColor,
          normalized
        );
        searchSubmit.style.borderRadius = px(
          lerp(expanded.searchButton.radius, currentSearchButton.width / 2, normalized)
        );
        searchSubmit.style.padding = '0';
        const expandedIconSize = expanded.searchSubmitIconViewport
          ? expanded.searchSubmitIconViewport.width
          : 23;
        searchSubmit.style.setProperty(
          '--pws-search-icon-current-size',
          px(lerp(expandedIconSize, compactSearchIconSize * 0.58, normalized))
        );
        searchSubmit.style.opacity = '1';
        searchSubmit.style.visibility = 'visible';
        searchSubmit.style.pointerEvents = 'auto';
        searchSubmit.style.transform = 'none';
        searchSubmit.style.zIndex = '1215';
      }

      if (header.classList.contains('pws-header--compact-account-icon-only')) {
        accountText.forEach((element) => {
          element.style.opacity = String(1 - hiddenProgress);
          element.style.visibility = hiddenProgress >= 0.995 ? 'hidden' : 'visible';
          element.style.pointerEvents = hiddenProgress >= 0.8 ? 'none' : 'auto';
        });
      }

      updateMetrics();
    }

    const animateScrollProgress = (timestamp) => {
      scrollAnimationFrame = null;

      if (!desktopMedia.matches || !stickyEnabled || !compactEnabled) {
        targetScrollProgress = 0;
        renderedScrollProgress = 0;
        scrollProgress = 0;
        scrollModeActivated = false;
        clearScrollPresentation(true);
        updateMetrics();
        return;
      }

      const elapsed = lastScrollFrameTime
        ? Math.min(48, Math.max(1, timestamp - lastScrollFrameTime))
        : 16.667;
      lastScrollFrameTime = timestamp;
      const smoothing = 1 - Math.exp(-elapsed / SCROLL_SMOOTHING_MS);

      renderedScrollProgress +=
        (targetScrollProgress - renderedScrollProgress) * smoothing;

      if (Math.abs(targetScrollProgress - renderedScrollProgress) <= SCROLL_EPSILON) {
        renderedScrollProgress = targetScrollProgress;
      }

      applyScrollProgress(renderedScrollProgress);

      if (Math.abs(targetScrollProgress - renderedScrollProgress) > SCROLL_EPSILON) {
        scrollAnimationFrame = window.requestAnimationFrame(animateScrollProgress);
      } else {
        lastScrollFrameTime = 0;
      }
    };

    const updateScrollTarget = () => {
      if (!desktopMedia.matches || !stickyEnabled || !compactEnabled) {
        targetScrollProgress = 0;
        renderedScrollProgress = 0;
        scrollProgress = 0;
        scrollModeActivated = false;
        if (scrollAnimationFrame) window.cancelAnimationFrame(scrollAnimationFrame);
        scrollAnimationFrame = null;
        lastScrollFrameTime = 0;
        clearScrollPresentation(true);
        updateMetrics();
        return;
      }

      if (!geometry) measureGeometry();
      if (!geometry) return;

      const scrollPosition = window.scrollY || window.pageYOffset || 0;

      /* Quando si torna all'inizio della pagina, la ricerca compatta si richiude
       * automaticamente e l'intestazione riprende lo stato originale. */
      if (
        header.classList.contains('pws-header--search-open') &&
        scrollPosition <= Math.max(2, compressionStart)
      ) {
        setSearchOpen(false);
      }

      if (scrollPosition > 0.5) scrollModeActivated = true;
      targetScrollProgress = clamp((scrollPosition - compressionStart) / compressionDistance);

      if (!scrollModeActivated && targetScrollProgress <= 0) {
        renderedScrollProgress = 0;
        scrollProgress = 0;
        clearScrollPresentation(true);
        updateMetrics();
        return;
      }

      if (!scrollAnimationFrame) {
        scrollAnimationFrame = window.requestAnimationFrame(animateScrollProgress);
      }
    };

    const updateScrollProgress = () => {
      updateScrollTarget();
    };

    const requestScrollUpdate = () => {
      updateScrollTarget();
    };

    /*
     * Sincronizzazione mega menu / sfocatura.
     * QUESTO BLOCCO È IDENTICO al tema sincronizzato caricato dall'utente.
     */
    const getMegaItemVisualState = (item) => {
      const submenu = item.querySelector('[data-meganav-menu]');
      const animation = submenu?.getAttribute('data-animation') || '';
      const animationState = submenu?.getAttribute('data-animation-state') || '';

      if (animation.endsWith('=>closed') || animationState === 'closed') return 'closed';

      if (
        animation.endsWith('=>open') ||
        animationState === 'open' ||
        animationState === 'ghost'
      ) {
        return 'open';
      }

      return 'closed';
    };

    const isMegaMenuOpen = () => {
      if (!desktopMedia.matches) return false;
      return megaItems.some((item) => getMegaItemVisualState(item) === 'open');
    };

    const isBasicDropdownOpen = () => {
      if (!desktopMedia.matches) return false;
      return basicDropdownDetails.some((details) => details.open);
    };

    const setMegaVisualState = (open) => {
      const shouldOpen = Boolean(open && desktopMedia.matches);
      body.classList.toggle('pws-mega-menu-open', shouldOpen);
      if (megaOverlay) megaOverlay.setAttribute('aria-hidden', String(!shouldOpen));
      updateMetrics();
    };

    const updateMegaState = () => {
      setMegaVisualState(isMegaMenuOpen() || isBasicDropdownOpen());
    };

    let megaStateQueued = false;
    const requestMegaStateUpdate = () => {
      if (megaStateQueued) return;
      megaStateQueued = true;

      const run = () => {
        megaStateQueued = false;
        updateMegaState();
      };

      if (typeof window.queueMicrotask === 'function') {
        window.queueMicrotask(run);
      } else {
        Promise.resolve().then(run);
      }
    };

    const megaObserver = new MutationObserver(requestMegaStateUpdate);
    if (navigation) {
      megaObserver.observe(navigation, {
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'aria-expanded', 'data-animation', 'data-animation-state']
      });
    }

    megaDetails.forEach((details) => {
      details.addEventListener('toggle', requestMegaStateUpdate);
    });

    basicDropdownDetails.forEach((details) => {
      details.addEventListener('toggle', requestMegaStateUpdate);
    });

    megaSubmenus.forEach((submenu) => {
      submenu.addEventListener('transitionrun', requestMegaStateUpdate);
      submenu.addEventListener('transitionstart', requestMegaStateUpdate);
      submenu.addEventListener('transitionend', requestMegaStateUpdate);
      submenu.addEventListener('transitioncancel', requestMegaStateUpdate);
      submenu.addEventListener('animationstart', requestMegaStateUpdate);
      submenu.addEventListener('animationend', requestMegaStateUpdate);
      submenu.addEventListener('animationcancel', requestMegaStateUpdate);
    });

    megaItems.forEach((item) => {
      item.addEventListener('mouseenter', requestMegaStateUpdate);
      item.addEventListener('mouseleave', requestMegaStateUpdate);
      item.addEventListener('focusin', requestMegaStateUpdate);
      item.addEventListener('focusout', requestMegaStateUpdate);
    });

    if (navigation) {
      navigation.addEventListener('mouseover', (event) => {
        const topLevelItem = event.target.closest('.navmenu-depth-1 > .navmenu-item');
        if (!topLevelItem) return;

        const topLevelDetails = topLevelItem.querySelector(':scope > [data-navmenu-details]');
        if (!topLevelDetails) setMegaVisualState(false);
        requestMegaStateUpdate();
      });
    }

    if (megaOverlay) {
      megaOverlay.addEventListener('click', () => {
        setMegaVisualState(false);
        window.setTimeout(requestMegaStateUpdate, 0);
      });
    }

    header.addEventListener('mouseover', requestMegaStateUpdate, { passive: true });
    header.addEventListener('focusin', requestMegaStateUpdate);
    header.addEventListener('click', () => window.setTimeout(requestMegaStateUpdate, 0));

    disclosures.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) closeDisclosureSiblings(details, header);
      });
    });

    if (compactSearchToggle) {
      compactSearchToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setSearchOpen(!header.classList.contains('pws-header--search-open'));
      });
    }

    if (searchSubmit) {
      searchSubmit.addEventListener('click', (event) => {
        if (scrollProgress < 0.995 || header.classList.contains('pws-header--search-open')) return;
        event.preventDefault();
        event.stopPropagation();
        setSearchOpen(true);
      });
    }

    if (searchClose) {
      searchClose.addEventListener('click', (event) => {
        event.preventDefault();
        setSearchOpen(false);
        if (searchSubmit && typeof searchSubmit.blur === 'function') searchSubmit.blur();
      });
    }

    document.addEventListener('click', (event) => {
      if (header.classList.contains('pws-header--search-open')) {
        const clickedSearch = searchShell && searchShell.contains(event.target);
        if (!clickedSearch) setSearchOpen(false);
      }

      disclosures.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.removeAttribute('open');
      });
    });

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      if (header.classList.contains('pws-header--search-open')) {
        setSearchOpen(false);
        if (searchSubmit && typeof searchSubmit.blur === 'function') searchSubmit.blur();
      }

      disclosures.forEach((details) => details.removeAttribute('open'));
      setMegaVisualState(false);
      window.setTimeout(requestMegaStateUpdate, 0);
    });

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener(
      'resize',
      () => {
        if (resizeTicking) return;
        resizeTicking = true;
        window.requestAnimationFrame(() => {
          resizeTicking = false;
          geometry = null;
          if (desktopMedia.matches) measureGeometry();
          requestScrollUpdate();
          updateMetrics();
        });
      },
      { passive: true }
    );

    const mediaChange = () => {
      setSearchOpen(false);
      setMegaVisualState(false);
      geometry = null;
      scrollProgress = 0;
      targetScrollProgress = 0;
      renderedScrollProgress = 0;
      scrollModeActivated = false;
      if (scrollAnimationFrame) window.cancelAnimationFrame(scrollAnimationFrame);
      scrollAnimationFrame = null;
      lastScrollFrameTime = 0;
      clearScrollPresentation(true);

      if (desktopMedia.matches) {
        measureGeometry();
        requestScrollUpdate();
      }

      updateMegaState();
      updateMetrics();
    };

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener('change', mediaChange);
    } else {
      desktopMedia.addListener(mediaChange);
    }



    if (desktopMedia.matches) measureGeometry();
    updateScrollProgress();
    updateMegaState();
    updateMetrics();
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-pws-header]')) initHeader(scope);
    scope.querySelectorAll?.('[data-pws-header]').forEach(initHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', () => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('[data-pws-header]').forEach((header) => {
        header.dispatchEvent(new Event('click', { bubbles: false }));
      });
    });
  });
  document.addEventListener('shopify:block:deselect', () => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('[data-pws-header]').forEach((header) => {
        header.dispatchEvent(new Event('click', { bubbles: false }));
      });
    });
  });
})();
